import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Typography,
} from "@mui/material";
import { TablePagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { formatNumber, toIntPercent, formatDuration } from "../../utils/constant";
import { Skeleton } from "@mui/material";

const TableSkeletonRows = ({ rows = 10 }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <TableRow key={index}>
        <TableCell><Skeleton width={160} /></TableCell>
        <TableCell><Skeleton width={60} height={24} /></TableCell>
        <TableCell><Skeleton width={40} /></TableCell>
        <TableCell><Skeleton width={90} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
        <TableCell><Skeleton width={80} /></TableCell>
        <TableCell><Skeleton width={100} /></TableCell>
      </TableRow>
    ))}
  </>
);

export const AllCustomersTable = ({
  tableLoading = false,
  data = [],
  totalCount = 0,
  page = 1,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange
}) => {
  const navigate = useNavigate();

  const handleRowClick = (row) => {
    navigate(`/dashboard/${encodeURIComponent(row.companyName)}`, {
      state: {
        status: row.status,
        apisSubscribed: row.apisSubscribed,
      },
    });
  };

  return (
    <Box>
      <Paper sx={{ width: "100%", borderRadius: 3, boxShadow: 2, p: 2 }}>
        <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
        }}
      >
          {/* Left side: Title + subtitle */}
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
            All Customers
          </Typography>

            <Typography variant="body2" color="text.secondary" 
            sx={{
                mb: 2,
                fontWeight: 400,
                fontSize: "1.4rem",
                color: "#424242",
              }}>
            Click on a customer to view detailed analytics
          </Typography>
        </Box>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {[
                  "Company Name",
                  "Status",
                  "APIs Subscribed",
                  "Total Calls",
                  "Success Rate",
                  "Credits Used",
                  "Avg Response",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#64748B",
                      borderBottom: "1px solid #E2E8F0",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableSkeletonRows rows={rowsPerPage} />
              ) : data.length > 0 ? (
                data.map((row, index) => (
                  <TableRow
                    hover
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >                
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customer-analytics/api-logs/${encodeURIComponent(row.companyName)}`);
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "primary.main",
                        display: "inline-block",
                      }}
                    >
                      {row.companyName}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 700,
                        fontSize: "11px",
                        backgroundColor:
                          row.status === "Active"
                            ? "#DCFCE7"
                            : "#E5E7EB",
                        color:
                          row.status === "Active"
                            ? "#166534"
                            : "#374151",
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {row.apisSubscribed}
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {formatNumber(row.totalCalls)}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color:
                        row.successRate >= 70
                          ? "#16A34A"
                          : "#F97316",
                    }}
                  >
                    {toIntPercent(row.successRate)}
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {formatNumber(row.creditUsed)}
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                     {formatDuration(row.avgResponseTimeMs)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, fontSize: "15px" }}>
                  No customers found
                </TableCell>
              </TableRow>
            )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1} 
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            onPageChange(1); 
            onRowsPerPageChange(Number(e.target.value));
          }}
          rowsPerPageOptions={[15, 25, 50, 100]}
          sx={{
            mt: 2,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "12px",
              fontWeight: 600,
            },
            "& .MuiTablePagination-select": {
              fontSize: "12px",
            },
          }}
        />

      </Paper>
    </Box>
  );
};
